// Non-sequential question grid + a separate "Completed" strip (completion order).
import { useMemo, useState } from 'react';
import type { Question, QuestionResult } from '@/types';
import { BIN_FILL, BIN_LABEL } from '@/lib/bins';
import { ClockIcon, typeIcon, typeLabel } from '@/components/exam/icons';
import { useExamStore } from '@/store/examStore';

interface QuestionPickerProps {
  questions: Question[];
  results: QuestionResult[];
  onPick: (questionId: string) => void;
}

const MCQ_FIRST = (q: Question) => (q.type === 'MCQ' ? 0 : 1);

function fmtAlloc(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.round(seconds / 60);
  return `${m}m`;
}

export function QuestionPicker({ questions, results, onPick }: QuestionPickerProps) {
  const completionOrder = useExamStore((s) => s.completionOrder);
  const attempts = useExamStore((s) => s.attempts);
  const [tooltipId, setTooltipId] = useState<string | null>(null);

  // Serial number derives from the paper's declared order.
  const serial = useMemo(() => {
    const map = new Map<string, number>();
    questions.forEach((q, i) => map.set(q.id, i + 1));
    return map;
  }, [questions]);

  const resultByQ = useMemo(() => {
    const m = new Map<string, QuestionResult>();
    for (const r of results) m.set(r.questionId, r);
    return m;
  }, [results]);

  // Sort: MCQ first, then descriptive; within group by allocatedSeconds asc.
  const sorted = useMemo(
    () =>
      [...questions].sort(
        (a, b) => MCQ_FIRST(a) - MCQ_FIRST(b) || a.allocatedSeconds - b.allocatedSeconds,
      ),
    [questions],
  );

  const pending = sorted.filter((q) => !resultByQ.has(q.id));
  const completed = completionOrder
    .map((id) => questions.find((q) => q.id === id))
    .filter((q): q is Question => Boolean(q));

  return (
    <div className="mx-auto max-w-3xl px-3 py-4">
      <h2 className="mb-2 text-sm font-semibold text-slate-700">Choose a question</h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {pending.map((q) => {
          const Icon = typeIcon(q.type);
          const num = serial.get(q.id);
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => onPick(q.id)}
              onMouseEnter={() => setTooltipId(q.id)}
              onMouseLeave={() => setTooltipId(null)}
              onTouchStart={() => setTooltipId(q.id)}
              className="relative flex flex-col items-start gap-1 rounded-xl border border-slate-200 bg-card p-3 text-left shadow-sm transition hover:border-slate-300 hover:shadow focus:outline-none focus:ring-2 focus:ring-slate-400"
              aria-label={`Question ${num}, ${typeLabel(q.type)}, ${fmtAlloc(q.allocatedSeconds)}`}
            >
              <div className="flex w-full items-center justify-between">
                <span className="text-base font-bold text-ink">Q{num}</span>
                <Icon className="h-4 w-4 text-slate-400" />
              </div>
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <ClockIcon className="h-3.5 w-3.5" /> {fmtAlloc(q.allocatedSeconds)}
              </span>
              {attempts[q.id]?.some((attempt) => attempt.reviewReason) && (
                <span className="text-xs font-semibold text-ink-soft">Flagged for teacher</span>
              )}

              {tooltipId === q.id && (q.topicName || q.subtopicName) && (
                <div
                  role="tooltip"
                  className="absolute left-2 top-full z-20 mt-1 w-max max-w-[12rem] rounded-lg bg-slate-900 px-2 py-1 text-fs-11 text-white shadow-lg"
                >
                  {q.topicName}
                  {q.subtopicName ? ` · ${q.subtopicName}` : ''}
                </div>
              )}
            </button>
          );
        })}
        {pending.length === 0 && (
          <p className="col-span-full rounded-lg px-3 py-4 text-center text-sm font-semibold" style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}>
            All questions attempted. Ready to submit!
          </p>
        )}
      </div>

      {completed.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Completed
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {completed.map((q) => {
              const num = serial.get(q.id);
              const bin = resultByQ.get(q.id)?.finalBin;
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => onPick(q.id)}
                  className="flex shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-card px-3 py-1.5 text-sm shadow-sm transition hover:shadow"
                  title={bin ? `Q${num} — ${BIN_LABEL[bin]}` : `Q${num}`}
                >
                  <span className="font-semibold text-slate-700">Q{num}</span>
                  {bin && (
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: BIN_FILL[bin] }}
                      aria-label={BIN_LABEL[bin]}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
